import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import HttpHelper from '../../utils/HttpHelper';
import { useParams } from 'react-router-dom';
import ApplicantsListTableRow from './ApplicantsListTableRow';
import { CandidateFilters } from '../Filter/CandidateFilterModal';

export interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
  sortable: boolean;
}

export interface Applicant {
  id: number;
  firstName: string;
  lastName: string;
  email?: string | null;
  status: number;
  totalGrade: number | null;
  schoolName?: string;
  degree?: string;
  gpa?: number;
  submittedAt?: string | null;
  immigrationStatus?: string | null;
  workEligibility?: string | null;
  ageEligibility?: string | null;
  availableBeforeJuly4?: string | null;
  // work experience years (computed by backend or ignored client-side)
  yearsOfExperience?: number | null;
}

interface ApplicantsListTableProps {
  onApplicantsFetched?: (applicants: Applicant[]) => void;
  activeFilters?: CandidateFilters;
}

const columns: readonly Column[] = [
  {
    id: 'name',
    label: 'Name',
    sortable: false,
  },
  {
    id: 'score',
    label: 'Score',
    align: 'left',
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    sortable: false,
  },
  {
    id: 'interviews',
    label: 'Interviews',
    align: 'center',
    sortable: false,
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    align: 'center',
    sortable: false,
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'center',
    sortable: false,
  },
];

const ApplicantsList: React.FC<ApplicantsListTableProps> = ({ onApplicantsFetched, activeFilters }) => {
  const { jobId } = useParams<{ jobId: string }>();

  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [scoreOrderAsc, setScoreOrderAsc] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const getApplicants = useCallback(() => {
    HttpHelper.get(`metrics/jobs/${jobId}`).then(response => {
      if (response.errorMessage) {
        alert(response.errorMessage);
      } else {
        setApplicants(response.applicants);
        if (onApplicantsFetched) {
          onApplicantsFetched(response.applicants);
        }
      }
    });
  }, [jobId, onApplicantsFetched]);

  useEffect(() => {
    getApplicants();
  }, [getApplicants]);

  const scoreSortingHandler = (e: React.MouseEvent<unknown>) => {
    setScoreOrderAsc(!scoreOrderAsc);
  };

  // ── Client-side filter application ──────────────────────────────────────
  const filteredApplicants = useMemo(() => {
    if (!activeFilters) return applicants;
    const f = activeFilters;

    return applicants.filter(a => {
      const fullName = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
      const email = (a.email || '').toLowerCase();

      // Search
      if (f.search) {
        const q = f.search.toLowerCase();
        if (!fullName.includes(q) && !email.includes(q)) return false;
      }

      // Status
      if (f.status !== '') {
        if (a.status !== Number(f.status)) return false;
      }

      // Score range
      if (f.minScore !== '') {
        const min = parseFloat(f.minScore);
        if ((a.totalGrade ?? -Infinity) < min) return false;
      }
      if (f.maxScore !== '') {
        const max = parseFloat(f.maxScore);
        if ((a.totalGrade ?? Infinity) > max) return false;
      }

      // Date applied
      if (f.dateAppliedFrom && a.submittedAt) {
        if (new Date(a.submittedAt) < new Date(f.dateAppliedFrom)) return false;
      }
      if (f.dateAppliedTo && a.submittedAt) {
        if (new Date(a.submittedAt) > new Date(f.dateAppliedTo)) return false;
      }

      // Degree level
      if (f.degreeLevel) {
        const deg = (a.degree || '').toLowerCase();
        if (!deg.includes(f.degreeLevel.toLowerCase())) return false;
      }

      // Field of study (matched against degree field for now)
      if (f.fieldOfStudy) {
        const deg = (a.degree || '').toLowerCase();
        if (!deg.includes(f.fieldOfStudy.toLowerCase())) return false;
      }

      // Eligibility — immigration
      if (f.immigrationStatus) {
        const imm = (a.immigrationStatus || '').toLowerCase();
        if (!imm.includes(f.immigrationStatus.toLowerCase())) return false;
      }

      // Work eligibility
      if (f.workEligibility !== '') {
        const we = (a.workEligibility || '').toLowerCase();
        const wantYes = f.workEligibility === 'yes';
        const isYes = we === 'yes' || we === 'true' || we === '1';
        if (wantYes !== isYes) return false;
      }

      // Age 15-30
      if (f.ageEligibility !== '') {
        const ae = (a.ageEligibility || '').toLowerCase();
        const wantYes = f.ageEligibility === 'yes';
        const isYes = ae === 'yes' || ae === 'true' || ae === '1';
        if (wantYes !== isYes) return false;
      }

      // Available before July 4
      if (f.availableBeforeJuly4 !== '') {
        const av = (a.availableBeforeJuly4 || '').toLowerCase();
        const wantYes = f.availableBeforeJuly4 === 'yes';
        const isYes = av === 'yes' || av === 'true' || av === '1';
        if (wantYes !== isYes) return false;
      }

      return true;
    });
  }, [applicants, activeFilters]);

  const sortedApplicants = useMemo(() => {
    // Add original index to preserve order within same scores
    const applicantsWithIndex = filteredApplicants.map((applicant, index) => ({
      ...applicant,
      originalIndex: index
    }));

    const result = applicantsWithIndex.sort((a, b) => {
      const scoreA = Number(a.totalGrade || 0);
      const scoreB = Number(b.totalGrade || 0);

      // Primary: sort by score
      const scoreDiff = scoreOrderAsc ? (scoreA - scoreB) : (scoreB - scoreA);

      if (scoreDiff !== 0) {
        return scoreDiff;
      }

      // Secondary: for ascending (lowest to highest), use reverse original order
      // For descending (highest to lowest), use normal original order
      const indexDiff = scoreOrderAsc ? (b.originalIndex - a.originalIndex) : (a.originalIndex - b.originalIndex);
      return indexDiff;
    });

    return result.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [scoreOrderAsc, filteredApplicants, page, rowsPerPage]);

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        margin: 'auto',
        marginTop: '8px',
      }}
    >
      <TableContainer sx={{ maxHeight: '600px' }}>
        <Table stickyHeader aria-label="sticky-table">
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sortDirection={
                    column.sortable ? (scoreOrderAsc ? 'asc' : 'desc') : false
                  }
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={true}
                      direction={scoreOrderAsc ? 'asc' : 'desc'}
                      onClick={scoreSortingHandler}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    <>{column.label}</>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedApplicants.map(applicant => {
              return (
                <ApplicantsListTableRow
                  key={applicant.id}
                  applicant={applicant}
                  columns={columns}
                  jobId={jobId}
                  onStatusUpdate={getApplicants}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredApplicants.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default ApplicantsList;
